// Document Generator Utility
// Supports PDF, Excel, and Word document generation for quotations

import { formatCurrency, formatDateTime } from './formatters';

// Generate PDF content for quotation
export const generateQuotationPDF = (quotation) => {
  const pdfContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>報價單 - ${quotation.quotation_id}</title>
      <style>
        body { font-family: 'Microsoft JhengHei', Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .company-info { margin-bottom: 20px; }
        .quotation-info { margin-bottom: 20px; }
        .customer-info { margin-bottom: 20px; }
        .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .items-table th { background-color: #f8f9fa; }
        .total-section { text-align: right; margin-top: 20px; }
        .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>報價單</h1>
        <h2>${quotation.quotation_id}</h2>
      </div>
      
      <div class="company-info">
        <h3>東昇工業設備有限公司</h3>
        <p>地址: 香港九龍灣宏光道1號創富中心15樓</p>
        <p>電話: +852 2345 6789 | 傳真: +852 2345 6790</p>
        <p>電郵: info@eastop.com.hk</p>
      </div>
      
      <div class="quotation-info">
        <p><strong>報價日期:</strong> ${quotation.quotation_date}</p>
        <p><strong>有效期至:</strong> ${quotation.expiry_date}</p>
        <p><strong>貨幣:</strong> ${quotation.currency}</p>
      </div>
      
      <div class="customer-info">
        <h3>客戶資料</h3>
        <p><strong>公司名稱:</strong> ${quotation.customer_name}</p>
        <p><strong>聯絡人:</strong> ${quotation.contact_person}</p>
        <p><strong>電郵:</strong> ${quotation.contact_email}</p>
        <p><strong>電話:</strong> ${quotation.contact_phone}</p>
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th>產品</th>
            <th>描述</th>
            <th>數量</th>
            <th>單價</th>
            <th>小計</th>
          </tr>
        </thead>
        <tbody>
          ${quotation.items?.map(item => `
            <tr>
              <td>${item.product_name}</td>
              <td>${item.description}</td>
              <td>${item.quantity} ${item.unit}</td>
              <td>${formatCurrency(item.unit_price)}</td>
              <td>${formatCurrency(item.subtotal)}</td>
            </tr>
          `).join('') || ''}
        </tbody>
      </table>
      
      <div class="total-section">
        <p><strong>小計:</strong> ${formatCurrency(quotation.subtotal)}</p>
        <p><strong>折扣:</strong> ${formatCurrency(quotation.discount_amount || 0)}</p>
        <p><strong>稅額:</strong> ${formatCurrency(quotation.tax_amount || 0)}</p>
        <p><strong>總額:</strong> ${formatCurrency(quotation.total_amount)}</p>
      </div>
      
      ${quotation.notes ? `
        <div class="notes">
          <h3>備註</h3>
          <p>${quotation.notes}</p>
        </div>
      ` : ''}
      
      <div class="footer">
        <p>此報價單有效期至 ${quotation.expiry_date}</p>
        <p>如有任何問題，請隨時與我們聯繫</p>
      </div>
    </body>
    </html>
  `;
  
  return pdfContent;
};

// Generate Excel content for quotation
export const generateQuotationExcel = (quotation) => {
  const excelData = {
    sheets: [
      {
        name: '報價單',
        data: [
          ['東昇工業設備有限公司', '', '', '', ''],
          ['地址: 香港九龍灣宏光道1號創富中心15樓', '', '', '', ''],
          ['電話: +852 2345 6789 | 傳真: +852 2345 6790', '', '', '', ''],
          ['', '', '', '', ''],
          ['報價單', '', '', '', ''],
          ['報價單號:', quotation.quotation_id, '', '', ''],
          ['報價日期:', quotation.quotation_date, '', '', ''],
          ['有效期至:', quotation.expiry_date, '', '', ''],
          ['貨幣:', quotation.currency, '', '', ''],
          ['', '', '', '', ''],
          ['客戶資料', '', '', '', ''],
          ['公司名稱:', quotation.customer_name, '', '', ''],
          ['聯絡人:', quotation.contact_person, '', '', ''],
          ['電郵:', quotation.contact_email, '', '', ''],
          ['電話:', quotation.contact_phone, '', '', ''],
          ['', '', '', '', ''],
          ['產品', '描述', '數量', '單價', '小計'],
          ...(quotation.items?.map(item => [
            item.product_name,
            item.description,
            `${item.quantity} ${item.unit}`,
            formatCurrency(item.unit_price),
            formatCurrency(item.subtotal)
          ]) || []),
          ['', '', '', '', ''],
          ['小計:', '', '', '', formatCurrency(quotation.subtotal)],
          ['折扣:', '', '', '', formatCurrency(quotation.discount_amount || 0)],
          ['稅額:', '', '', '', formatCurrency(quotation.tax_amount || 0)],
          ['總額:', '', '', '', formatCurrency(quotation.total_amount)],
          ['', '', '', '', ''],
          ['備註:', quotation.notes || '', '', '', '']
        ]
      }
    ]
  };
  
  return excelData;
};

// Generate Word content for quotation
export const generateQuotationWord = (quotation) => {
  const wordContent = `
    <!DOCTYPE html>
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head>
      <meta charset="utf-8">
      <title>報價單 - ${quotation.quotation_id}</title>
      <style>
        body { font-family: 'Microsoft JhengHei', Arial, sans-serif; }
        .header { text-align: center; margin-bottom: 30px; }
        .section { margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #000; padding: 8px; }
        th { background-color: #f0f0f0; }
        .total { text-align: right; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>報價單</h1>
        <h2>${quotation.quotation_id}</h2>
      </div>
      
      <div class="section">
        <h3>東昇工業設備有限公司</h3>
        <p>地址: 香港九龍灣宏光道1號創富中心15樓</p>
        <p>電話: +852 2345 6789 | 傳真: +852 2345 6790</p>
        <p>電郵: info@eastop.com.hk</p>
      </div>
      
      <div class="section">
        <p><strong>報價日期:</strong> ${quotation.quotation_date}</p>
        <p><strong>有效期至:</strong> ${quotation.expiry_date}</p>
        <p><strong>貨幣:</strong> ${quotation.currency}</p>
      </div>
      
      <div class="section">
        <h3>客戶資料</h3>
        <p><strong>公司名稱:</strong> ${quotation.customer_name}</p>
        <p><strong>聯絡人:</strong> ${quotation.contact_person}</p>
        <p><strong>電郵:</strong> ${quotation.contact_email}</p>
        <p><strong>電話:</strong> ${quotation.contact_phone}</p>
      </div>
      
      <div class="section">
        <h3>報價項目</h3>
        <table>
          <thead>
            <tr>
              <th>產品</th>
              <th>描述</th>
              <th>數量</th>
              <th>單價</th>
              <th>小計</th>
            </tr>
          </thead>
          <tbody>
            ${quotation.items?.map(item => `
              <tr>
                <td>${item.product_name}</td>
                <td>${item.description}</td>
                <td>${item.quantity} ${item.unit}</td>
                <td>${formatCurrency(item.unit_price)}</td>
                <td>${formatCurrency(item.subtotal)}</td>
              </tr>
            `).join('') || ''}
          </tbody>
        </table>
      </div>
      
      <div class="section total">
        <p><strong>小計:</strong> ${formatCurrency(quotation.subtotal)}</p>
        <p><strong>折扣:</strong> ${formatCurrency(quotation.discount_amount || 0)}</p>
        <p><strong>稅額:</strong> ${formatCurrency(quotation.tax_amount || 0)}</p>
        <p><strong>總額:</strong> ${formatCurrency(quotation.total_amount)}</p>
      </div>
      
      ${quotation.notes ? `
        <div class="section">
          <h3>備註</h3>
          <p>${quotation.notes}</p>
        </div>
      ` : ''}
    </body>
    </html>
  `;
  
  return wordContent;
};

// Download file utility
export const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Print quotation
export const printQuotation = (quotation) => {
  const printWindow = window.open('', '_blank');
  const content = generateQuotationPDF(quotation);
  
  printWindow.document.write(content);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
};
